import { Component, Prop } from 'vue-property-decorator'
import { CommonLaboratoryResponse, EnumResponse } from '@/api/services/common.dto'
import { AssayModalAssayWithValuableConceptResponse } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.dto'
import { assaysViewAssayService } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.service'
import {
    initialShipmentRulesModalAssayRelationshipsState, ShipmentRulesModalAssayRelationshipsState
} from '@/modules/configurationModule/detail/shipments/shipment-rules/components/shipment-rules-modal-assay-relationships/shipment-rules-modal-assay-relationships.model'
import { ShipmentRuleEntityTypeEnum } from '@/modules/configurationModule/detail/shipments/shipment-rules/models/shipment-rules.model'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import { shipmentRulesAuxiliaryService } from '@/modules/configurationModule/detail/shipments/shipment-rules/services/shipment-rules-auxiliary/shipment-rules-auxiliary.service'
import {
    ShipmentRuleAssayCreateRequest,
    ShipmentRuleAssayResponse
} from '@/modules/configurationModule/detail/shipments/shipment-rules/services/shipment-rule-assay/shipment-rule-assay.dto'
import TranslatedEntityBadge from '@/components/common/TranslatedEntityBadge.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import { shipmentRuleAssayService } from '@/modules/configurationModule/detail/shipments/shipment-rules/services/shipment-rule-assay/shipment-rule-assay.service'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import {
    ManualShipmentRuleAssayCreateRequest
} from '@/modules/configurationModule/detail/shipments/shipment-rules/services/manual-shipment-rule-assay/manual-shipment-rule-assay.dto'
import { manualShipmentRuleAssayService } from '@/modules/configurationModule/detail/shipments/shipment-rules/services/manual-shipment-rule-assay/manual-shipment-rule-assay.service'

@Component({
    components: {
        TranslatedEntityBadge,
        VtGrid,
        ConfigurationSectionHeader,
        GridDisplayOptions
    }
})
export default class ShipmentRulesModalAssayRelationships extends BaseCrudServerSideComponent<ShipmentRuleAssayResponse> implements BaseCrud {
    @Prop({ default: null }) readonly originLaboratory!: CommonLaboratoryResponse
    @Prop() readonly shipmentRuleId!: number
    @Prop() readonly entityType!: EnumResponse
    @Prop() readonly isManual!: boolean

    state: ShipmentRulesModalAssayRelationshipsState
    constructor() {
        super()
        this.state = initialShipmentRulesModalAssayRelationshipsState(this.isManual, this.shipmentRuleId)
    }

    async created(): Promise<void> {
        if (this.entityType) {
            this.state.comboOptions.assayStates = await this.getAllSubportionInternalStatesByLaboratory(this.originLaboratory?.labId)
        }
        if (this.originLaboratory) {
            this.state.comboOptions.assays = await this.getAssays()
        }
    }

    getContextMenuItems(params: GetContextMenuItemsParams): (string | MenuItemDef)[] {
        return [
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.disableDeleteButton ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            }
        ]
    }

    get disableAddButton(): boolean {
        return !this.state.assay
    }

    get visibleStatesCombo(): boolean {
        return this.entityType?.code !== ShipmentRuleEntityTypeEnum.SUBPORTION
    }

    get disableDeleteButton(): boolean {
        return !this.state.selectedRows.length
    }

    async getAssays(): Promise<AssayModalAssayWithValuableConceptResponse[]> {
        const assaysResponse = await this.$_toCallUnique(assaysViewAssayService.getAssays(this.originLaboratory?.labId))
        return assaysResponse ?? []
    }

    async getAllSubportionInternalStatesByLaboratory(laboratoryId: Nullable<number>): Promise<EnumResponse[]> {
        const response = await this.$_toCallUnique(shipmentRulesAuxiliaryService.getAllSubportionsInternalStatesByLaboratory(laboratoryId))
        return response ?? []
    }

    async createManualShipmentRuleAssay(shipmentRuleAssay: ManualShipmentRuleAssayCreateRequest): Promise<StrictNullable<ShipmentRuleAssayResponse>> {
        return this.$_toCallUnique(manualShipmentRuleAssayService.createShipmentRuleAssay(shipmentRuleAssay))
    }

    async createShipmentRuleAssay(shipmentRuleAssay: ShipmentRuleAssayCreateRequest): Promise<Nullable<ShipmentRuleAssayResponse>> {
        return this.$_toCallUnique(shipmentRuleAssayService.createShipmentRuleAssay(shipmentRuleAssay))
    }

    async deleteOperation(rows: ShipmentRuleAssayResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$tc('configuration.grid.deleteConfirmation')
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$tc('configuration.grid.deleteQuestion')
            const labelDeleteAction:string = this.$tc('configuration.actions.delete')
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                await this._deleteBatchOperation(rows)
            }
        }
    }

    async createShipmentRuleAssayRelationship(): Promise<void> {
        const shipmentManualRuleAssayCreateRequest: ManualShipmentRuleAssayCreateRequest = {
            assayId: this.state.assay?.id,
            subportionInternalStates: this.state.assayState?.code,
            manualShipmentRuleId: this.shipmentRuleId
        }
        const shipmentRuleAssayCreateRequest: ShipmentRuleAssayCreateRequest = {
            assayId: this.state.assay?.id,
            subportionInternalStates: this.state.assayState?.code,
            shipmentRuleId: this.shipmentRuleId
        }
        const createShipmentRuleAssayResponse = this.isManual
            ? await this.createManualShipmentRuleAssay(shipmentManualRuleAssayCreateRequest)
            : await this.createShipmentRuleAssay(shipmentRuleAssayCreateRequest)
        if (createShipmentRuleAssayResponse) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.reloadData()
        }
    }

    async _deleteBatchOperation(rows: ShipmentRuleAssayResponse[]): Promise<boolean> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((shipmentRuleAssay: ShipmentRuleAssayResponse) => Number(shipmentRuleAssay?.id))
        }
        let response: StrictNullable<EntityIdentifierWithErrorResponse[]>
        if (this.isManual) {
            response = await this.$_toCallUnique(manualShipmentRuleAssayService.deleteShipmentRuleAssayBatch(rowsToDelete))
        } else {
            response = await this.$_toCallUnique(shipmentRuleAssayService.deleteShipmentRuleAssayBatch(rowsToDelete))
        }
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
            this.reloadData()
            return true
        } else {
            return false
        }
    }
}
